import { api } from "../../api"
import { BoardViewModel, makeBoardViewModel } from "../../boards/api/board"
import { TodoProjectResponse, TodoResponse, TodoTaskResponse } from "./todo-response"
import { makeTodoApiEndpoints } from "./endpoints"
import { TodoViewModel, makeTodoViewModel } from "./todo"
import { getQueryArgsByEndpoint, getQueryArgsByEndpoints, hasPendingMutations } from "../../utils/rtk-query-util"
import { OptionType } from "../../tasks/api/task-response"

const apiEndpoints = makeTodoApiEndpoints()
const pendingEndpointNames = [
  "updateTodoCustomers",
  "updateTodoParticipants",
  "updateTodoSuppliers",
  "updateTodoWorkspaces",
  "updateTodoProject",
  "updateTodoTask",
]
export const todoApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getTodos: builder.query<TodoViewModel[], TodoQueryParams | void>({
      query: (params) => ({
        method: "GET",
        url: apiEndpoints.todos(),
        params: params ? new URLSearchParams(params) : undefined,
      }),
      transformResponse: (response: { todos: TodoResponse[] }) => response.todos.map(makeTodoViewModel),
      providesTags: (result = []) => ["Todo", ...result.map(({ id }) => ({ type: "Todo", id } as const))],
    }),
    createTodo: builder.mutation<TodoViewModel, NewTodoData>({
      query: (todoData) => ({
        method: "POST",
        url: apiEndpoints.todos(),
        body: todoData,
      }),
      transformResponse: makeTodoViewModel,
      invalidatesTags: ["Todo"],
    }),
    updateTodoInfo: builder.mutation<TodoViewModel, { todoId: string } & TodoInfoUpdateData>({
      query: ({ todoId, ...todoData }) => ({
        method: "PUT",
        url: apiEndpoints.todoItem(todoId),
        body: todoData,
      }),
      transformResponse: makeTodoViewModel,
      async onQueryStarted({ todoId, ...todoData }, { dispatch, queryFulfilled, getState }) {
        const queryData = getQueryArgsByEndpoint(getState, "getTodos")
        if (!queryData) return
        const { args } = queryData
        const patchResult = dispatch(
          todoApi.util.updateQueryData("getTodos", args, (draft) => {
            const todo = draft.find((t) => t.id === todoId)
            if (todo) {
              Object.assign(todo, todoData)
            }
          })
        )
        try {
          await queryFulfilled
          const state = getState()
          if (!hasPendingMutations(state, { endpointNames: "updateTodoInfo" }))
            dispatch(todoApi.util.invalidateTags(["Todo", { type: "Todo", id: todoId }]))
        } catch {
          // rollback all optimistic updates
          patchResult.undo()
        }
      },
    }),
    updateTodoStatus: builder.mutation<UpdateTodoWithBoardReturnData, { todoId: string } & TodoStatusUpdateData>({
      query: ({ todoId, ...todoData }) => ({
        method: "PATCH",
        url: apiEndpoints.todoStatus(todoId),
        body: todoData,
      }),
      transformResponse: transformTodoWithBoard,
      onQueryStarted: async ({ todoId, ...todoData }, { dispatch, queryFulfilled, getState }) => {
        const queryData = getQueryArgsByEndpoint(getState, "getTodos")
        if (!queryData) return
        const { args } = queryData
        const patchResult = dispatch(
          todoApi.util.updateQueryData("getTodos", args, (draft) => {
            const todo = draft.find((t) => t.id === todoId)
            if (todo) {
              Object.assign(todo, todoData)
              // Optimistically update completed timestamp
              if (todoData.completed === true) {
                todo.completed = new Date().toISOString()
              } else {
                todo.completed = null
              }
            }
          })
        )
        try {
          await queryFulfilled
          const state = getState()
          if (!hasPendingMutations(state, { endpointNames: "updateTodoStatus" }))
            dispatch(
              todoApi.util.invalidateTags([
                "Todo",
                { type: "Todo", id: todoId },
                { type: "Board", id: todoData.boardId },
              ])
            )
        } catch {
          patchResult.undo()
        }
      },
    }),
    updateTodoProject: builder.mutation<UpdateTodoWithBoardReturnData, { todoId: string } & TodoProjectUpdateData>({
      query: ({ todoId, project, boardId }) => ({
        method: "PATCH",
        url: apiEndpoints.projectRelation(todoId),
        body: { projectId: project ? project.id : null, boardId },
      }),
      transformResponse: transformTodoWithBoard,
      async onQueryStarted({ todoId, project, boardId }, { dispatch, queryFulfilled, getState }) {
        const queryData = getQueryArgsByEndpoint(getState, "getTodos")
        if (!queryData) return
        const { args } = queryData
        const patchResult = dispatch(
          todoApi.util.updateQueryData("getTodos", args, (draft) => {
            const todo = draft.find((t) => t.id === todoId)
            if (todo) {
              const savedProjectId = todo.project?.id
              todo.project = project
              if (savedProjectId !== project?.id) {
                todo.task = null // remove task relation if project is changed since tasks are project specific
              }
            }
          })
        )
        try {
          await queryFulfilled
          const state = getState()
          if (!hasPendingMutations(state, { endpointNames: pendingEndpointNames }))
            todoApi.util.invalidateTags(["Todo", { type: "Todo", id: todoId }, { type: "Board", id: boardId }])
        } catch {
          // rollback all optimistic updates
          patchResult.undo()
        }
      },
    }),
    updateTodoTask: builder.mutation<UpdateTodoWithBoardReturnData, { todoId: string } & TodoTaskUpdateData>({
      query: ({ todoId, task, boardId }) => ({
        method: "PATCH",
        url: apiEndpoints.taskRelation(todoId),
        body: { taskId: task ? task.id : null, boardId },
      }),
      transformResponse: transformTodoWithBoard,
      async onQueryStarted({ todoId, task, boardId }, { dispatch, queryFulfilled, getState }) {
        const queryData = getQueryArgsByEndpoint(getState, "getTodos")
        if (!queryData) return
        const { args } = queryData
        const patchResult = dispatch(
          todoApi.util.updateQueryData("getTodos", args, (draft) => {
            const todo = draft.find((t) => t.id === todoId)
            if (todo) {
              todo.task = task
            }
          })
        )
        try {
          await queryFulfilled
          const state = getState()
          if (!hasPendingMutations(state, { endpointNames: pendingEndpointNames }))
            todoApi.util.invalidateTags(["Todo", { type: "Todo", id: todoId }, { type: "Board", id: boardId }])
        } catch {
          // rollback all optimistic updates
          patchResult.undo()
        }
      },
    }),
    updateTodoWorkspaces: builder.mutation<UpdateTodoWithBoardReturnData, { todoId: string } & TodoWorkspaceUpdateData>(
      {
        query: ({ todoId, workspaces, boardId, columnPosition }) => ({
          method: "PATCH",
          url: apiEndpoints.workspacesRelation(todoId),
          body: { workspaces: workspaces.map((w) => w.id), boardId, columnPosition },
        }),
        transformResponse: transformTodoWithBoard,
        async onQueryStarted({ todoId, workspaces, boardId }, { dispatch, queryFulfilled, getState }) {
          const queryData = getQueryArgsByEndpoint(getState, "getTodos")
          if (!queryData) return
          const { args } = queryData
          const patchResult = dispatch(
            todoApi.util.updateQueryData("getTodos", args, (draft) => {
              const todo = draft.find((t) => t.id === todoId)
              if (todo) {
                todo.workspaces = workspaces
              }
            })
          )
          try {
            await queryFulfilled
            const state = getState()
            if (!hasPendingMutations(state, { endpointNames: pendingEndpointNames }))
              dispatch(
                todoApi.util.invalidateTags(["Todo", { type: "Todo", id: todoId }, { type: "Board", id: boardId }])
              )
          } catch {
            // rollback all optimistic updates
            patchResult.undo()
          }
        },
      }
    ),
    updateTodoResponsible: builder.mutation<
      UpdateTodoWithBoardReturnData,
      { todoId: string } & TodoResponsibleUpdateData
    >({
      query: ({ todoId, responsible, boardId, columnPosition }) => ({
        method: "PATCH",
        url: apiEndpoints.responsibleRelation(todoId),
        body: { responsible: responsible.map((r) => r.id), boardId, columnPosition },
      }),
      transformResponse: transformTodoWithBoard,
      async onQueryStarted({ todoId, responsible, boardId }, { dispatch, queryFulfilled, getState }) {
        const queryEntries = getQueryArgsByEndpoints(getState, "getTodos")
        if (!queryEntries) return
        const patchResults = queryEntries.map(({ args }) =>
          dispatch(
            todoApi.util.updateQueryData("getTodos", args, (draft) => {
              const todo = draft.find((t) => t.id === todoId)

              if (todo) {
                todo.responsible = responsible
              }
            })
          )
        )

        try {
          await queryFulfilled
          // TODO: Remove this if no issues are found after testing
          // we're invalidating tags manually in the components for this mutation
          // const state = getState()
          // if (!hasPendingMutations(state, { endpointNames: pendingEndpointNames }))
          //   dispatch(
          //     todoApi.util.invalidateTags(["Todo", { type: "Todo", id: todoId }, { type: "Board", id: boardId }])
          //   )
        } catch {
          // rollback all optimistic updates
          patchResults.forEach((patchResult) => patchResult.undo())
        }
      },
    }),
    updateTodoCustomers: builder.mutation<UpdateTodoWithBoardReturnData, { todoId: string } & TodoCustomerUpdateData>({
      query: ({ todoId, customers, boardId, columnPosition }) => ({
        method: "PATCH",
        url: apiEndpoints.customersRelation(todoId),
        body: { customers: customers.map((c) => c.id), boardId, columnPosition },
      }),
      transformResponse: transformTodoWithBoard,
      async onQueryStarted({ todoId, customers, boardId }, { dispatch, queryFulfilled, getState }) {
        const queryEntries = getQueryArgsByEndpoints(getState, "getTodos")
        if (!queryEntries) return
        const patchResults = queryEntries.map(({ args }) =>
          dispatch(
            todoApi.util.updateQueryData("getTodos", args, (draft) => {
              const todo = draft.find((t) => t.id === todoId)
              if (todo) {
                todo.customers = customers
              }
            })
          )
        )
        try {
          await queryFulfilled
          const state = getState()
          if (!hasPendingMutations(state, { endpointNames: pendingEndpointNames }))
            dispatch(
              todoApi.util.invalidateTags(["Todo", { type: "Todo", id: todoId }, { type: "Board", id: boardId }])
            )
        } catch {
          // rollback all optimistic updates
          patchResults.forEach((patchResult) => patchResult.undo())
        }
      },
    }),
    deleteTodo: builder.mutation<boolean, string>({
      query: (todoId) => ({
        method: "DELETE",
        url: apiEndpoints.todoItem(todoId),
      }),
      transformResponse: () => true,
      invalidatesTags: ["Todo"],
    }),
  }),
})

function transformTodoWithBoard({ todo, board }: UpdateTodoWithBoardResponseData) {
  return {
    todo: makeTodoViewModel(todo),
    board: board ? makeBoardViewModel(board) : null,
  }
}
export const {
  useGetTodosQuery,
  useCreateTodoMutation,
  useUpdateTodoInfoMutation,
  useUpdateTodoStatusMutation,
  useUpdateTodoProjectMutation,
  useUpdateTodoTaskMutation,
  useUpdateTodoWorkspacesMutation,
  useUpdateTodoResponsibleMutation,
  useUpdateTodoCustomersMutation,
  useDeleteTodoMutation,
  usePrefetch,
} = todoApi

type TodoBaseQuery = { completed?: "true" | "false" }
type OrgTodoQueryParams = { organisation: string }
type CustomerTodoQueryParams = { customer: string }
type ResponsibleTodoQueryParams = { responsible: string }
type WorkspaceTodoQueryParams = { workspace: string }
type ProjectTodoQueryParams = { project: string }
type TaskTodoQueryParams = { task: string }

export type TodoQueryParams = TodoBaseQuery &
  (
    | {}
    | OrgTodoQueryParams
    | CustomerTodoQueryParams
    | ResponsibleTodoQueryParams
    | WorkspaceTodoQueryParams
    | ProjectTodoQueryParams
    | TaskTodoQueryParams
  )

export type NewTodoData = {
  title: string
  description?: string | null
  organisation?: string | null
  responsible?: string[] // membership id if todo is in organisation, empty if todo is in personal context
  dueDate?: string | null
  project?: string | null
  task?: string | null
  workspaces?: string[]
  customers?: string[]
  enableTimeComponent?: boolean
}

export type TodoInfoUpdateData = {
  title?: string
  description?: string | null
  dueDate?: string | null
  enableTimeComponent?: boolean
  ganttBarColor?: string | null
}

export type TodoStatusUpdateData = {
  completed: boolean
  boardId?: string
}

export type TodoProjectUpdateData = {
  project: TodoProjectResponse | null
  boardId?: string
}

export type TodoTaskUpdateData = {
  task: TodoTaskResponse | null
  boardId?: string
}

export type TodoWorkspaceUpdateData = {
  workspaces: OptionType[]
  boardId?: string
  columnPosition?: number
}

export type TodoResponsibleUpdateData = {
  responsible: OptionType[]
  boardId?: string
  columnPosition?: number
}

export type TodoCustomerUpdateData = {
  customers: OptionType[]
  boardId?: string
  columnPosition?: number
}

type UpdateTodoWithBoardReturnData = {
  todo: TodoViewModel
  board: BoardViewModel | null
}

type UpdateTodoWithBoardResponseData = {
  todo: TodoResponse
  board: any
}
